/**
 * SchoolSlugCard — super-admin surfaced public URL slug editor.
 * Lets a tenant define a vanity slug (e.g. "fwbc") that resolves public
 * pages via /s/{slug}. Visible only to super admins (set_slug is super-only).
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Link as LinkIcon, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import { useAuthStore } from '@/lib/store';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchoolSlugCard({ tenantId }) {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const isSuperAdmin = user && (user.is_super_admin === true || user.role === 'super_admin'
    || (Array.isArray(user.permissions) && (user.permissions.includes('*') || user.permissions.includes('super_admin'))));

  const { data: tenant } = useQuery({
    queryKey: ['school-tenant-slug', tenantId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/school/tenants/${tenantId}`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
    enabled: !!tenantId,
  });

  const [slug, setSlug] = useState('');
  React.useEffect(() => { if (tenant?.slug) setSlug(tenant.slug); }, [tenant?.slug]);

  const save = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API_URL}/api/admin/school-tenants/${tenantId}/slug`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed');
      return data;
    },
    onSuccess: (d) => {
      toast.success(`Slug saved: ${d.slug}`);
      setSlug(d.slug);
      qc.invalidateQueries({ queryKey: ['school-tenant-slug', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const backfill = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API_URL}/api/admin/school-tenants/backfill-slugs`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed');
      return data;
    },
    onSuccess: (d) => {
      toast.success(`Backfilled ${d.updated} slug${d.updated === 1 ? '' : 's'}`);
      qc.invalidateQueries({ queryKey: ['school-tenant-slug', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  if (!isSuperAdmin) return null;

  const publicUrl = slug ? `${window.location.origin}/s/${slug}` : '';
  const copy = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success('URL copied');
  };

  return (
    <Card data-testid="school-slug-card" className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <LinkIcon className="h-4 w-4 text-indigo-500" /> Public URL slug
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid md:grid-cols-[1fr_auto_auto] gap-2 items-end">
          <div>
            <Label className="text-xs">Vanity slug</Label>
            <div className="flex gap-1 items-center">
              <span className="text-xs text-slate-500 shrink-0">{window.location.origin}/s/</span>
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="fwbc"
                data-testid="school-slug-input"
                className="h-9"
              />
            </div>
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !slug} data-testid="school-slug-save-btn" size="sm">
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Save
          </Button>
          <Button onClick={() => backfill.mutate()} disabled={backfill.isPending} variant="outline" size="sm" data-testid="school-slug-backfill-btn">
            {backfill.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Backfill all
          </Button>
        </div>
        {tenant?.slug && (
          <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
            <code className="text-xs flex-1 truncate" data-testid="school-slug-public-url">{publicUrl}</code>
            <Button variant="ghost" size="sm" onClick={copy} data-testid="school-slug-copy-btn">
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        <p className="text-[11px] text-slate-500">
          Shareable link. Resolves to this school's public dashboard. Use lowercase letters, numbers, hyphens.
        </p>
      </CardContent>
    </Card>
  );
}
